/**
 * Translation-registry hooks (#50 stage 2). Mirrors useCompleteness.
 * Mutations invalidate the registry list and the change-requests
 * surfaces (registry writes ride maker-checker like completeness rules).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { translationTermsApi } from '../api/translationTerms';
import { ApiError } from '../api/client';
import type {
  DocumentLanguage,
  Page,
  TranslationTerm,
  TranslationTermCreateInput,
  TranslationTermDomain,
  TranslationTermUpdateInput,
  UUID,
} from '../api/types';

const tKeys = {
  all: ['translation-terms'] as const,
  list: (domain?: TranslationTermDomain, language?: DocumentLanguage) =>
    ['translation-terms', 'list', domain ?? 'all', language ?? 'all'] as const,
};

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: tKeys.all });
  qc.invalidateQueries({ queryKey: ['change-requests'] });
}

export function useTranslationTerms(
  domain?: TranslationTermDomain,
  language?: DocumentLanguage,
) {
  return useQuery<Page<TranslationTerm>, ApiError>({
    queryKey: tKeys.list(domain, language),
    queryFn: () => translationTermsApi.list(domain, language),
  });
}

export function useCreateTranslationTerm() {
  const qc = useQueryClient();
  return useMutation<TranslationTerm, ApiError, TranslationTermCreateInput>({
    mutationFn: translationTermsApi.create,
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateTranslationTerm(id: UUID) {
  const qc = useQueryClient();
  return useMutation<TranslationTerm, ApiError, TranslationTermUpdateInput>({
    mutationFn: (input) => translationTermsApi.update(id, input),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteTranslationTerm() {
  const qc = useQueryClient();
  return useMutation<TranslationTerm, ApiError, UUID>({
    mutationFn: translationTermsApi.delete,
    onSuccess: () => invalidate(qc),
  });
}
